import dynamic from 'next/dynamic';
import BreadCrumbs, { IBreadcrumbs } from 'ui/components/molecules/Breadcrumbs';
import FaqAccordion, { IFaq } from 'ui/components/molecules/FaqAccordion';
import PromoListSection from 'ui/components/organism/PromoListSection';
import InfoSection from 'ui/components/organism/InfoSection';
import promoPageView from 'ui/lib/gtm/promoPageView';
import { sendDataInChunks } from 'ui/lib/collectData';
import { useEffect, useRef, useState } from 'react';
import useElementVisibility from 'ui/hooks/useElementVisibility';
import { MdOutlineArrowBack } from 'react-icons/md';
import { useRouter } from 'next/router';
import { NewsHero } from 'ui/interface';
import Head from 'next/head';
import generateImageResizer from '../../../helpers/utils/generateImageResizer';
import { FEATURE } from '../../../constants/emarsys/feature';
import useImpressionListPromo from '../../../hooks/emarsys/promo-list/useImpressionListPromo';
import { PAGE_NAME_PROMO } from '../../../constants/emarsys/promo';

const ScrollDownButton = dynamic(() => import('ui/components/atoms/ScrollDownButton'), {
  ssr: true,
});
const PromoBannerSection = dynamic(() => import('ui/components/organism/PromoBannerSection'), {
  ssr: true,
});

interface PromoListingPageProps {
  promoHero: NewsHero;
  promoList: {
    id?: string;
    contentCategory?: {
      name?: string;
    };
    slug?: string;
    titlePage?: string;
    articleType?: string;
    startDate?: string;
    endDate?: string;
    heroImageLink?: string;
    altImage?: string;
    tag: {
      id?: string;
      tagName?: string;
    };
  }[];
  categoryList?: {
    name: string;
    path: string;
    priority?: string;
    description?: string;
  }[];
  faqs?: {
    title: string;
    faqList: IFaq[];
  };
  totalData?: number;
  totalPages?: number;
  breadcrumbItems?: IBreadcrumbs[];
  cityName?: string;
  info?: {
    title: string;
    desc: string;
  };
  paginationBaseUrl?: string;
  pageSize?: number;
  showBackButton?: boolean;
  tabClassname?: string;
  page?: number;
  category?: string;
  feature?: string;
}

const PromoListingPage = ({
  promoHero,
  promoList,
  categoryList,
  faqs,
  totalData,
  totalPages,
  breadcrumbItems,
  cityName,
  info,
  paginationBaseUrl,
  pageSize,
  showBackButton = false,
  tabClassname,
  page,
  category = '',
  feature = '',
}: PromoListingPageProps) => {
  const [collectDataLayer, setCollectDataLayer] = useState([]);
  const promoListRef = useRef(null);
  const infoPromoImpressionRef = useRef(null);
  const faqImpressionRef = useRef(null);
  const isPromoListVisible = useElementVisibility(promoListRef, 0.215);
  const [isVisible, setIsVisible] = useState(true);
  const router = useRouter();

  const handleScroll = () => {
    const scrolled = document.documentElement.scrollTop;

    // set the button visibility based on the scroll position
    setIsVisible(scrolled < window.innerHeight - 50);
  };

  const scrollDown = () => {
    window.scrollTo({
      top: 625,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    // add event listener to detect scroll
    window.addEventListener('scroll', handleScroll);

    // remove event listener when component unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (collectDataLayer?.length > 0) {
      sendDataInChunks(
        collectDataLayer,
        1,
        (chunk) => {
          promoPageView(chunk);
        },
        setCollectDataLayer,
        ''
      );
    }
  }, [collectDataLayer]);

  // For Impression List Promo
  if (feature === FEATURE.LISTING_PROMO) {
    useImpressionListPromo({
      firstRef: promoListRef,
      pageName: PAGE_NAME_PROMO.PROMO_LISTING_PAGE,
      pageTitle: promoHero?.promoTitle,
      breadcrumbItems: breadcrumbItems,
      sectionName: 'list promo',
      newParams: {
        ...(breadcrumbItems?.[2]
          ? { menu_cat_lvl2: breadcrumbItems?.[2].name?.toLowerCase() }
          : {}),
        category: category?.toLowerCase(),
        page_number: page,
      },
    });
  }

  // For Impression Info Promo
  if (feature === FEATURE.LISTING_PROMO) {
    useImpressionListPromo({
      firstRef: infoPromoImpressionRef,
      pageName: PAGE_NAME_PROMO.PROMO_LISTING_PAGE,
      pageTitle: promoHero?.promoTitle,
      breadcrumbItems: breadcrumbItems,
      sectionName: 'informasi promo toyota',
      newParams: {
        ...(breadcrumbItems?.[2]
          ? { menu_cat_lvl2: breadcrumbItems?.[2].name?.toLowerCase() }
          : {}),
        category: category?.toLowerCase(),
        page_number: page,
      },
    });
  }

  // For Impression FAQ Promo
  if (feature === FEATURE.LISTING_PROMO) {
    useImpressionListPromo({
      firstRef: faqImpressionRef,
      pageName: PAGE_NAME_PROMO.PROMO_LISTING_PAGE,
      pageTitle: promoHero?.promoTitle,
      breadcrumbItems: breadcrumbItems,
      sectionName: 'promo faq',
      newParams: {
        ...(breadcrumbItems?.[2]
          ? { menu_cat_lvl2: breadcrumbItems?.[2].name?.toLowerCase() }
          : {}),
        category: category?.toLowerCase(),
        page_number: page,
      },
    });
  }

  return (
    <>
      <Head>
        {(promoHero?.heroImageLink || promoHero?.heroLinkResizedImage) && (
          <link
            rel="preload"
            as="image"
            href={promoHero.heroImageLink || promoHero.heroLinkResizedImage}
          />
        )}
        {promoList
          ?.filter(
            (promo, index) =>
              promo.heroImageLink && (promoHero?.heroImageLink ? index < 2 : index < 4)
          )
          .map((promo) => (
            <link
              rel="preload"
              as="image"
              href={generateImageResizer(promo.heroImageLink, 170, 'webp')}
            />
          ))}
      </Head>
      {/* Breadcrumb for web screen */}
      <BreadCrumbs isMobileScreen={false} items={breadcrumbItems} />
      {showBackButton && (
        <div className="container border-b-[0.25px] border-reliableBlack10 flex justify-start items-center w-full pt-2 pb-2 sticky top-[60px] md:top-[111px] z-30 bg-white md:hidden">
          <button className="h-full" onClick={() => router.back()} aria-label="back button">
            <MdOutlineArrowBack size={24} color="#3D4043" />
          </button>
        </div>
      )}

      {/* Promo banner section */}
      {promoHero && (
        <PromoBannerSection
          promoHero={promoHero}
          city={cityName}
          handleCollectDataLayer={(data) => setCollectDataLayer([data])}
        />
      )}

      {!isPromoListVisible && (
        <ScrollDownButton className={isVisible ? 'block' : 'hidden'} onClick={scrollDown} />
      )}

      {/* Promo list section */}
      <div ref={promoListRef}>
        <PromoListSection
          categoryList={categoryList}
          promoList={promoList}
          totalData={totalData}
          totalPages={totalPages}
          paginationBaseUrl={paginationBaseUrl}
          pageSize={pageSize}
          page={page}
          categoryEmarsys={category}
          tabClassname={tabClassname}
          hasBanner={Boolean(promoHero?.heroImageLink || promoHero?.heroLinkResizedImage)}
          pageTitle={promoHero?.promoTitle}
          feature={FEATURE.LISTING_PROMO}
          breadcrumbItems={breadcrumbItems}
        />
      </div>

      {/* Promo info section */}
      <div ref={infoPromoImpressionRef}>
        <InfoSection
          info={info}
          feature={feature}
          impressionData={{
            pageTitle: promoHero?.promoTitle,
            category: category,
            page_number: page,
            breadcrumbItems: breadcrumbItems,
          }}
        />
      </div>

      {/* Promo FAQ section */}
      <div ref={faqImpressionRef}>
        <FaqAccordion
          title={faqs.title}
          faqs={faqs.faqList}
          containerClassname="mt-10 mb-[47.72px] lg:my-20"
        />
      </div>

      {/* Breadcrumb for mobile screen */}
      <BreadCrumbs isMobileScreen={true} items={breadcrumbItems} />
    </>
  );
};

export default PromoListingPage;
